/**
 * Conversation Tracking Utilities for InboundX Chat
 *
 * This utility handles conversation tracking and analytics for the InboundX chat system.
 * It formats and submits conversation data to the backend tracking service including:
 * - Complete conversation history with timestamps
 * - Page context information (URL, title, domain)
 * - Message metadata and source information
 * - Session and thread tracking identifiers
 * - Formatted source references and snippets
 *
 * The utility ensures consistent data formatting across different chat services
 * and provides detailed analytics for conversation analysis and improvement.
 */

import { Analytics } from '../../analytics';
import type { MessageSourceType } from '../../analytics/types';
import type { ConversationPair } from '../../services/chatStorage';
import type { VisitorProfile } from '../../services/inboundx';
import { logger } from '../core/logger';

export const trackConversation = async (
    conversationPairs: ConversationPair[],
    query: string,
    output: string,
    messageId: string,
    _sessionId?: string,
    threadId?: string,
    sources?: Array<{ title?: string; url?: string; snippet?: string; content?: string }>,
    messageSource?: MessageSourceType,
    visitorProfile?: VisitorProfile | null
): Promise<void> => {
    try {
        // Format conversation history from conversation pairs
        const conversationHistory: Array<{
            role: string;
            content: string;
            timestamp: string;
        }> = [];

        conversationPairs.forEach((pair) => {
            // Add question
            conversationHistory.push({
                role: 'user',
                content: pair.question.content,
                timestamp: new Date(pair.question.timestamp).toISOString(),
            });

            // Add answer if it exists
            if (pair.answer) {
                conversationHistory.push({
                    role: 'assistant',
                    content: pair.answer.content,
                    timestamp: new Date(pair.answer.timestamp).toISOString(),
                });
            }
        });

        // Add the current exchange to history
        conversationHistory.push(
            {
                role: 'user',
                content: query,
                timestamp: new Date().toISOString(),
            },
            {
                role: 'assistant',
                content: output,
                timestamp: new Date().toISOString(),
            }
        );

        // Format sources if available
        const formattedSources = sources
            ? sources.map((source) => ({
                  title: source.title || '',
                  url: source.url || '',
                  snippet: source.snippet || source.content || '',
              }))
            : [];

        // Use the new analytics system to track conversations
        await Analytics.trackConversation({
            query: query,
            messageId: messageId,
            conversation_history: conversationHistory,
            output: output,
            sources: formattedSources,
            sources_count: formattedSources.length,
            has_sources: formattedSources.length > 0,
            threadId: threadId,
            message_source: messageSource || 'user_typed',
            visitor_profile: visitorProfile,
        });
    } catch (error) {
        logger.error('InboundXChat: Error tracking conversation:', error);
    }
};
